import { useState, useRef, useEffect } from 'react';
import { Box, Avatar, Tooltip } from '@mui/material';
import { getUser } from '@/util/getUser';
import { useFetch } from '@/hooks/useFetch';
import { addCommentUrl, deleteCommentUrl, updateCommentUrl } from '@/constants';
import { useAppContext } from '@/context/context';
import { Comment } from '@sm-dashboard/types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatTimeAgo } from '@/util/dateUtils';
import { Button, Input, IconButton, Dialog, Typography } from '@sm-dashboard/ui-components';

type FeedCommentsProps = {
  postId: number;
  CommentsList: Comment[];
  showCommentsList: boolean;
  showCommentInput: boolean;
  onCommentInputClose: () => void;
  CloseCommentSection: () => void;
  userImage?: string;
  addNewComment: (newComment: Comment) => void;
  deleteComment: (commentId: number) => void;
};

function FeedComments({
  postId,
  CommentsList,
  userImage,
  addNewComment,
  deleteComment,
  showCommentsList,
  showCommentInput,
  onCommentInputClose
}: FeedCommentsProps) {
  const [comment, setComment] = useState('');
  const user = getUser();
  const { fetchData, loading } = useFetch();
  const { setSnackbar, userPermission } = useAppContext();
  const [isEditing, setIsEditing] = useState<number | null>(null);
  const [editedComment, setEditedComment] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const commentIdToDeleteRef = useRef<number | null>(null);
  const [sortedComments, setSortedComments] = useState<Comment[]>([]);
  const commentsBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sorted = [...CommentsList].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateA - dateB;
    });
    setSortedComments(sorted);
  }, [CommentsList]);

  const scrollToBottom = () => {
    if (commentsBoxRef.current) {
      commentsBoxRef.current.scrollTop = commentsBoxRef.current.scrollHeight;
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) {
      setSnackbar({
        message: 'לא ניתן לשמור תגובה ריקה',
        severity: 'error',
        open: true
      });
      return;
    }
    try {
      const response = await fetchData({
        url: `${addCommentUrl}/${postId}`,
        method: 'POST',
        data: {
          comment: comment,
          shaliachId: user?.id,
          postId: postId
        }
      });

      const newComment: Comment = {
        id: response.id,
        comment: comment,
        shaliachId: user?.id || 0,
        postId: postId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        Shluchim: {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          profileImage: userImage || ''
        }
      };

      setSortedComments(prevComments => [...prevComments, newComment]);
      requestAnimationFrame(scrollToBottom);
      addNewComment(newComment);
      commentIdToDeleteRef.current = newComment.id;

      setSnackbar({
        message: 'התגובה נוספה בהצלחה',
        severity: 'success',
        open: true
      });

      setComment('');
      onCommentInputClose();
    } catch (error) {
      setSnackbar({
        message: 'התגובה נכשלה',
        severity: 'error',
        open: true
      });
      console.error('Error submitting comment:', error);
    }
  };
  const handleEditComment = async (commentId: number) => {
    try {
      if (!editedComment.trim()) {
        setSnackbar({
          message: 'לא ניתן לשמור תגובה ריקה',
          severity: 'error',
          open: true
        });
        return;
      }

      await fetchData({
        url: `${updateCommentUrl}/${commentId}`,
        method: 'PUT',
        data: {
          comment: editedComment
        }
      });

      setSortedComments(
        sortedComments.map(comment =>
          comment.id === commentId
            ? { ...comment, comment: editedComment, updatedAt: new Date().toISOString() }
            : comment
        )
      );

      setSnackbar({
        message: 'התגובה עודכנה בהצלחה',
        severity: 'success',
        open: true
      });

      setIsEditing(null);
    } catch (error) {
      setSnackbar({
        message: 'עדכון התגובה נכשל',
        severity: 'error',
        open: true
      });
      console.error('Error updating comment:', error);
    }
  };

  const handleDeleteComment = async () => {
    if (commentIdToDeleteRef.current === null) return;
    try {
      await fetchData({
        url: `${deleteCommentUrl}/${commentIdToDeleteRef.current}`,
        method: 'DELETE'
      });

      setSortedComments(sortedComments.filter(comment => comment.id !== commentIdToDeleteRef.current));
      deleteComment(commentIdToDeleteRef.current);
      setSnackbar({
        message: 'התגובה נמחקה בהצלחה',
        severity: 'success',
        open: true
      });
    } catch (error) {
      setSnackbar({
        message: 'מחיקת התגובה נכשלה',
        severity: 'error',
        open: true
      });
      console.error('Error deleting comment:', error);
    } finally {
      setOpenDeleteDialog(false);
      commentIdToDeleteRef.current = null;
    }
  };

  return (
    <>
      {showCommentsList && (
        <Box
          ref={commentsBoxRef}
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'start'
          }}
        >
          {sortedComments &&
            (sortedComments.length === 0 ? (
              <Box sx={{ p: 1, textAlign: 'center', fontSize: '14px' }}>אין תגובות, בוא תהיה הראשון...</Box>
            ) : (
              sortedComments.map((comment: Comment) => (
                <Box
                  key={comment.id}
                  sx={{
                    mb: 1.5,
                    maxWidth: isEditing === comment.id ? '100%' : '87%',
                    minWidth: 0,
                    width: '100%'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 1 }}>
                    <Avatar src={comment.Shluchim?.profileImage || undefined} sx={{ width: 32, height: 32 }}>
                      {!comment.Shluchim?.profileImage &&
                        (comment.Shluchim?.firstName && comment.Shluchim?.lastName
                          ? `${comment.Shluchim.firstName.charAt(0)}${comment.Shluchim.lastName.charAt(0)}`
                          : 'A')}
                    </Avatar>
                    <Box
                      sx={{
                        backgroundColor: '#f0f2f5',
                        borderRadius: '18px',
                        padding: '8px 12px',
                        position: 'relative',
                        mb: 1,
                        maxWidth: '100%',
                        minWidth: 0,
                        wordWrap: 'break-word',
                        '&:hover .comment-actions': {
                          opacity: 1
                        }
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 600,
                          fontSize: '13px',
                          display: 'block',
                          mb: 0.5
                        }}
                      >
                        {comment.Shluchim?.firstName} {comment.Shluchim?.lastName}
                      </Box>
                      {isEditing === comment.id ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                          <Input
                            appearance="plain"
                            multiline
                            maxRows={10}
                            value={editedComment}
                            onChange={e => setEditedComment(e.target.value)}
                            autoFocus
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={async () => {
                                await handleEditComment(comment.id);
                                setIsEditing(null);
                              }}
                              loading={loading}
                              disabled={!editedComment.trim()}
                            >
                              שמור
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => setIsEditing(null)}>
                              ביטול
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box
                            component="p"
                            sx={{
                              margin: 0,
                              fontSize: '15px',
                              lineHeight: 1.3,
                              wordWrap: 'break-word',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'pre-wrap'
                            }}
                          >
                            {comment.comment}
                          </Box>
                          <Box
                            className="comment-actions"
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              right: -40,
                              transform: 'translateY(-50%)',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              display: 'flex',
                              flexDirection: 'column'
                            }}
                          >
                            {comment.shaliachId === user?.id && (
                              <Tooltip title="ערוך תגובה" arrow>
                                <IconButton
                                  variant="text"
                                  color="secondary"
                                  shape="circle"
                                  aria-label="ערוך תגובה"
                                  onClick={() => {
                                    setIsEditing(comment.id);
                                    setEditedComment(comment.comment);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {(comment.shaliachId === user?.id ||
                              userPermission?.isAdmin ||
                              userPermission?.isSuperAdmin) && (
                              <Tooltip title="מחק תגובה" arrow>
                                <IconButton
                                  color="error"
                                  shape="circle"
                                  aria-label="מחק תגובה"
                                  onClick={() => {
                                    setOpenDeleteDialog(true);
                                    commentIdToDeleteRef.current = comment.id;
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </>
                      )}
                      <Box
                        component="span"
                        sx={{
                          fontSize: '12px',
                          color: '#888',
                          display: 'block',
                          mt: 0.5,
                          textAlign: 'right'
                        }}
                      >
                        {formatTimeAgo(comment.createdAt, comment.updatedAt)}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))
            ))}
        </Box>
      )}
      {showCommentInput && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, mb: 1 }}>
          <Avatar src={userImage || undefined} sx={{ mr: 0.5, ml: 1 }}>
            {!userImage &&
              (user?.firstName && user?.lastName ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}` : 'A')}
          </Avatar>
          <Input
            appearance="plain"
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="כתוב תגובה..."
            multiline
            minRows={1}
            maxRows={10}
            autoFocus
          />
          <Box sx={{ mr: 1 }}>
            <Button onClick={submitComment} loading={loading} disabled={!comment.trim()}>
              שלח
            </Button>
          </Box>
        </Box>
      )}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="אישור מחיקה"
        cancelAction
        primaryAction={{ label: 'מחק', onClick: handleDeleteComment, loading: loading, color: 'error' }}
      >
        <Typography variant="body" color="text.secondary">
          האם אתה בטוח שברצונך למחוק את התגובה?
        </Typography>
      </Dialog>
    </>
  );
}

export default FeedComments;
